import { ColumnConfig } from "@app/shared/waffles";
import { restrictByFields, TableType, ViewableField } from "@app/viewable-fields";
import { formatPhoneNumber, formatUnconvertedDateCustom, TooltipWrapper } from "../../shared";
import { OmniusCargoColumnType } from "../../shared/griddle/CargoColumns";
import { RoleRestricted, Roles } from "../../user";
import { EntityTypeEnum } from "../enumerators";
import { ProjectRunConfigurationResult } from "../projectRunConfigurationResult";
import { ProjectionProvider } from "../results/projection-provider";
import { ServicingProvider } from "../results/servicing-provider";
import { GainsPercentEnum, ImpactProviderTableData, NcpEnum, NPF } from "./types";
import { JSX } from "react";

const validVAColumnNameLookup: { [key: string]: string } = {
  "VAClaimAmount": "vaClaimAmount",
  "VAClaimAmountDisplay": "vaClaimAmountDisplay",
  "VAClaimsVolumeDisplay": "vaClaimsVolumeDisplay",
  "VANewPatientClaims": "vaNewPatientClaims",
  "VAParticipationDisplay": "vaParticipationDisplay"
};

const translatePercentGains = (percentGains: GainsPercentEnum): string => {
  switch (percentGains) {
    case GainsPercentEnum.All:
      return "All % Gains";
    case GainsPercentEnum.Zero:
      return "0% Gains";
    case GainsPercentEnum.Nonzero:
      return "> 0% Gains";
  }
};

const translateNcpFilter = (ncpValue: NcpEnum): string => {
  switch (ncpValue) {
    case NcpEnum.All:
      return "All Closer Non-Contracted Providers";
    case NcpEnum.NextClosest:
      return "Next Closest Non-Contracted Providers";
    case NcpEnum.NotNextClosest:
      return "Other Closer Non-Contracted Providers";
  }
};

const getEntityIconClasses = (entityType: string, additionalClasses?: string): string => {
  let iconClasses: string;
  switch (entityType) {
    case EntityTypeEnum.Facility.toLowerCase():
      iconClasses = "fa fa-building";
      break;
    case EntityTypeEnum.Individual.toLowerCase():
    default:
      iconClasses = "fa fa-user-md";
      break;
  }

  if (additionalClasses) {
    iconClasses.concat(additionalClasses);
  }

  return iconClasses;
};

// only display 'Individual' or 'Facility' indicators
const getEntityTypeDescription = (entityType: string): string => {
  switch (entityType) {
    case EntityTypeEnum.Facility.toLowerCase():
    case EntityTypeEnum.Individual.toLowerCase():
      return entityType;
    default:
      return null;
  }
};

const toCamelCase = (displayName: string): string => {
  if (displayName in validVAColumnNameLookup) {
    return validVAColumnNameLookup[displayName];
  } else {
    return displayName
      .split(/(?=[A-Z])/)
      .map((word, index) => {
        if (index === 0) {
          return word.toLowerCase();
        } else {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }
      })
      .join('');
  }
}

const buildAdequacyProvidersColumnConfig = (
  viewableFields: ViewableField[],
  tableType: TableType,
  showNppes: boolean,
  showMipsScore: boolean,
  showPciData: boolean,
  showVaClaimsVolumeColumn: boolean,
  showTricareClaimsVolumeColumn: boolean,
  showAvgDist?: boolean,
  showAvgTime?: boolean,
  showProjectionReason?: boolean,
  isNextClosestImpactConfig?: boolean,
  showExclusionSource?: boolean,
  showLocationConfidenceScores?: boolean,
  showMotiveAppropriatenessScore?: boolean,
  showNewPatientClaims?: boolean,
  showCareJourneyScores?: boolean
): ColumnConfig => {
  let config: ColumnConfig = {};
  if (!viewableFields || viewableFields.length === 0) {
    return config;
  }

  const fieldsWithoutColumns = [
    NPF.EntityType,
    NPF.NPI,
    NPF.TIN,
    NPF.OrganizationName,

    NPF.Address2,
    NPF.City,
    NPF.State,
    NPF.Zip,
    NPF.Phone,
    NPF.Fax,

    NPF.DeactivatedNPI,
    NPF.MedicareOptOut,
    NPF.OIGExcluded,
    NPF.OIGWaiver,
    NPF.Precluded,
  ];

  const fieldsThatShouldShowAvailability = [
    NPF.DeactivatedNPI,
    NPF.MedicareOptOut,
    NPF.OIGExcluded,
    NPF.OIGWaiver,
    NPF.Precluded,
  ];

  // These aren't in viewable fields
  config = {
    exclusionVerifiedSources: {
      displayText: "Exclusion Verified Sources",
      hidden: !showExclusionSource,
      noSort: true,
      testId: "exclusion-verified-sources",
    },
    lastUpdate: {
      displayText: showExclusionSource ? "Excluded Date" : "Targeted Date",
      hidden: !showProjectionReason,
      noSort: true,
      testId: "projection-last-update",
    }, 
    lastUpdateBy: {
      displayText: showExclusionSource ? "Excluded By" : "Targeted By",
      hidden: !showProjectionReason,
      noSort: true,
      testId: "projection-last-update-by",
    },
    name: {
      displayText: "Name",
      hidden: false,
      testId: "name",
    },
    projectionReason: {
      displayText: "Reason",
      hidden: !showProjectionReason,
      noSort: true,
      testId: "projection-reason",
    },
    projectionReasonDescription: {
      displayText: "Comment",
      hidden: !showProjectionReason,
      noSort: true,
      testId: "projection-description",
    },
    terminationReason: {
      displayText: "Reason",
      hidden: tableType !== TableType.ProviderTerminations,
      noSort: true,
      testId: "termination-reason",
    },
  };

  // viewable fields also includes cargo columns
  for (const field of viewableFields) {
    if (fieldsWithoutColumns.includes(field.backingFieldName as NPF)) {
      if (field.visible && fieldsThatShouldShowAvailability.includes(field.backingFieldName as NPF)) {
        config.availability = {
          displayText: "Availability",
          hidden: false,
          noSort: true,
          testId: "availability",
        };
      }
      continue;
    }
    const isCargo = field.isCargoColumn;
    const camelCase = isCargo ? field.defaultDisplayName : toCamelCase(field.backingFieldName)
    config[camelCase] = {
      cargoTitle: isCargo ? field.defaultDisplayName : undefined,
      displayText: field.displayName || field.defaultDisplayName,
      hidden: field.fieldExclude || !(field.visible || field.alwaysVisible) || fieldsWithoutColumns.includes(field.backingFieldName as NPF),
      testId: field.displayName.replace(/ /g, "-").toLowerCase(),
    };
  }

  // special NCPF config...
  if (tableType === TableType.NextClosestNetworkProviders) {
    config.averageDistance = {
      ...config.averageDistance,
      hidden: !(tableType === TableType.NextClosestNetworkProviders && showAvgDist),
      tooltip: "Average distance from provider to members without access",
    };
    config.averageTime = {
      ...config.averageTime,
      hidden: !(tableType === TableType.NextClosestNetworkProviders && showAvgTime),
      tooltip: "Average time from provider to members without access",
    };
    config.isServicing = {
      ...config.isServicing,
      hidden: tableType !== TableType.NextClosestNetworkProviders,
      tooltip: "Provides access to other members",
    };
    config.memberCount = {
      ...config.memberCount,
      hidden: tableType !== TableType.NextClosestNetworkProviders,
      tooltip: "Closest provider for this many members without access",
    };
  }

  // one-off tooltip...
  if (config.daysSinceLastAttested) {
    config.daysSinceLastAttested.tooltip = "Days since most recent attestation received; including validations, invalidations and enrichments";
  }

  // special visibility rules...
  if (config.nppesCertificationDate && config.nppesLastUpdatedDate) {
    config.nppesCertificationDate.hidden = !showNppes || !viewableFields.find((f) => f.backingFieldName === NPF.NppesCertificationDate && f.visible && !f.fieldExclude);
    config.nppesLastUpdatedDate.hidden = !showNppes || !viewableFields.find((f) => f.backingFieldName === NPF.NppesLastUpdatedDate && f.visible && !f.fieldExclude);
  }

  if (config.mipsScore) {
    config.mipsScore.hidden = !showMipsScore || !viewableFields.find((f) => f.backingFieldName === NPF.MipsScore && f.visible && !f.fieldExclude);
  }

  if (config.commercialClaimsVolumeDisplay) {
    config.commercialClaimsVolumeDisplay.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.CommercialClaimsVolume && f.visible && !f.fieldExclude);
    config.commercialClaimsVolumeDisplay.tooltip = getClaimsVolumeDefinitions();
  }

  if (config.commercialNewPatientClaims) {
    config.commercialNewPatientClaims.hidden = !showPciData || !showNewPatientClaims || !viewableFields.find((f) => f.backingFieldName === NPF.CommercialNewPatientClaims && f.visible && !f.fieldExclude);
    config.commercialNewPatientClaims.tooltip = getNewPatientClaimsDefinitions();
  }

  if (config.medicaidClaimsVolumeDisplay) {
    config.medicaidClaimsVolumeDisplay.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.MedicaidClaimsVolume && f.visible && !f.fieldExclude);
    config.medicaidClaimsVolumeDisplay.tooltip = getClaimsVolumeDefinitions();
  }

  if (config.medicaidNewPatientClaims) {
    config.medicaidNewPatientClaims.hidden = !showPciData || !showNewPatientClaims || !viewableFields.find((f) => f.backingFieldName === NPF.MedicaidNewPatientClaims && f.visible && !f.fieldExclude);
    config.medicaidNewPatientClaims.tooltip = getNewPatientClaimsDefinitions();
  }

  if (config.medicareClaimsVolumeDisplay) {
    config.medicareClaimsVolumeDisplay.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.MedicareClaimsVolume && f.visible && !f.fieldExclude);
    config.medicareClaimsVolumeDisplay.tooltip = getClaimsVolumeDefinitions();
  }

  if (config.medicareNewPatientClaims) {
    config.medicareNewPatientClaims.hidden = !showPciData || !showNewPatientClaims || !viewableFields.find((f) => f.backingFieldName === NPF.MedicareNewPatientClaims && f.visible && !f.fieldExclude);
    config.medicareNewPatientClaims.tooltip = getNewPatientClaimsDefinitions();
  }

  if (config.vaClaimsVolumeDisplay) {
      config.vaClaimsVolumeDisplay.hidden = !showVaClaimsVolumeColumn || !viewableFields.find((f) => f.backingFieldName === NPF.VAClaimsVolume && f.visible && !f.fieldExclude);
    config.vaClaimsVolumeDisplay.tooltip = getClaimsVolumeDefinitions();
  }

  if (config.vaNewPatientClaims) {
    config.vaNewPatientClaims.hidden = !showVaClaimsVolumeColumn || !showPciData || !showNewPatientClaims || !viewableFields.find((f) => f.backingFieldName === NPF.VANewPatientClaims && f.visible && !f.fieldExclude);
    config.vaNewPatientClaims.tooltip = getNewPatientClaimsDefinitions();
  }

  if (config.tricareClaimsVolumeDisplay) {
      config.tricareClaimsVolumeDisplay.hidden = !showTricareClaimsVolumeColumn || !viewableFields.find((f) => f.backingFieldName === NPF.TricareClaimsVolume && f.visible && !f.fieldExclude);
    config.tricareClaimsVolumeDisplay.tooltip = getClaimsVolumeDefinitions();
  }

  if (config.tricareNewPatientClaims) {
    config.tricareNewPatientClaims.hidden = !showTricareClaimsVolumeColumn || !showPciData || !showNewPatientClaims || !viewableFields.find((f) => f.backingFieldName === NPF.TricareNewPatientClaims && f.visible && !f.fieldExclude);
    config.tricareNewPatientClaims.tooltip = getNewPatientClaimsDefinitions();
  }

  if (config.totalClaimsVolumeDisplay) {
    config.totalClaimsVolumeDisplay.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.TotalClaimsVolume && f.visible && !f.fieldExclude);
    config.totalClaimsVolumeDisplay.tooltip = getClaimsVolumeDefinitions();
  }

  if (config.totalNewPatientClaims) {
    config.totalNewPatientClaims.hidden = !showPciData || !showNewPatientClaims || !viewableFields.find((f) => f.backingFieldName === NPF.TotalNewPatientClaims && f.visible && !f.fieldExclude);
    config.totalNewPatientClaims.tooltip = getNewPatientClaimsDefinitions();
  }

  if (config.locationConfidenceScoresDisplay) {
    config.locationConfidenceScoresDisplay.hidden = !showLocationConfidenceScores || !viewableFields.find((f) => f.backingFieldName === NPF.LocationConfidenceScore && f.visible && !f.fieldExclude);
    config.locationConfidenceScoresDisplay.tooltip = getLocationConfidenceScoresDefinitions();
  }


  if (config.effectivenessRank) {
    config.effectivenessRank.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.EffectivenessRank && f.visible && !f.fieldExclude);
    config.effectivenessRank.tooltip = getEfficiencyAndEffectivenessDefinitions(NPF.EffectivenessRank);
  }

  if (config.efficiencyRank) {
    config.efficiencyRank.hidden = !showPciData || !viewableFields.find((f) => f.backingFieldName === NPF.EfficiencyRank && f.visible && !f.fieldExclude);
    config.efficiencyRank.tooltip = getEfficiencyAndEffectivenessDefinitions(NPF.EfficiencyRank);
  }

  if (config.motiveScore) {
    config.motiveScore.displayElement = (
      <>
        <TooltipWrapper text={getMotiveScoreDefinitions()} position="bottom">
          <i className="fa fa-info-circle waffles-tooltip-indicator" />
        </TooltipWrapper>
        <img src={`${window.customUIBaseUrl}/images/motive-logo-yellow.svg`} alt="Motive Logo" height={"20"} className={"align-top mx-1"} />
        {config.motiveScore.displayText}
      </>)
    config.motiveScore.hidden = !showMotiveAppropriatenessScore || !viewableFields.find((f) => f.backingFieldName === NPF.MotiveScore && f.visible && !f.fieldExclude);
    config.motiveScore.tooltip = null;
  }

  if (config.careJourneyScoresCostScore) {
    config.careJourneyScoresCostScore.displayElement = (
      <div style={{minWidth: "175px"}}>
        <TooltipWrapper text={getCareJourneyDefinitions()} position="bottom">
          <i className="fa fa-info-circle waffles-tooltip-indicator" />
        </TooltipWrapper>
        <img src={`${window.customUIBaseUrl}/images/carejourney logo.svg`} alt="CareJourney " width="110" height="30" className="align-top mx-1" />
        {"Cost"}
      </div>);
    config.careJourneyScoresCostScore.hidden = !showCareJourneyScores || !viewableFields.find((f) => f.backingFieldName === NPF.CareJourneyScoresCostScore && f.visible && !f.fieldExclude);
    config.careJourneyScoresCostScore.tooltip = null;
  }

  if (config.careJourneyScoresQualityScore) {
    config.careJourneyScoresQualityScore.displayElement = (
      <div style={{ minWidth: "200px" }}>
        <TooltipWrapper text={getCareJourneyDefinitions()} position="bottom">
          <i className="fa fa-info-circle waffles-tooltip-indicator" />
        </TooltipWrapper>
        <img src={`${window.customUIBaseUrl}/images/carejourney logo.svg`} alt="CareJourney " width="120" height="30" className="align-top mx-1" />
        {"Quality"}
      </div>);
    config.careJourneyScoresQualityScore.hidden = !showCareJourneyScores || !viewableFields.find((f) => f.backingFieldName === NPF.CareJourneyScoresQualityScore && f.visible && !f.fieldExclude);
    config.careJourneyScoresQualityScore.tooltip = null;
  }

  // next closest impact provider rules...
  if (isNextClosestImpactConfig) {
    config.totalAverageDistance.hidden = !showAvgDist;
    config.totalAverageDistance.tooltip = "The average distance for which the provider is closer than the closest network provider for that member, but is not necessarily the member's closest provider";
    config.totalAverageTime.hidden = !showAvgTime;
    config.totalAverageTime.tooltip = "The average time for which the provider is closer than the closest network provider for that member, but is not necessarily the member's closest provider";
    config.totalMemberCount.tooltip = "The count of members for whom the provider is closer than the next closest network provider";
    config.averageDistance.hidden = !showAvgDist;
    config.averageDistance.tooltip = "The average distance for which the provider is the next closest provider for the member";
    config.averageTime.hidden = !showAvgTime;
    config.averageTime.tooltip = "The average time for which the provider is the next closest provider for the member";
    config.memberCount.tooltip = "The count of members for whom the provider is the next closest provider";

    config.specialtyCode.hidden = true; // is not used in next closest network providers
  }

  if (config.availability) {
    config.availability.noSort = true;
  }

  return config;
};

const buildCommonImpactProviderColumns = (provider: ServicingProvider, viewableFields: ViewableField[], runConfiguration: ProjectRunConfigurationResult): Partial<ImpactProviderTableData> => {
  /* eslint-disable sort-keys */
  return {
    specialtyCode: provider.specialtyCode,
    specialty: provider.specialty,
    address: restrictByFields(viewableFields, [NPF.Address, NPF.Address2, NPF.City, NPF.State, NPF.Zip, NPF.Phone, NPF.Fax], (has) => (
      <>
        {has(NPF.Address) && provider.address ? <>{provider.address}<br /></> : null}
        {has(NPF.Address2) && provider.address2 ? <>{provider.address2}<br /></> : null}
        {has(NPF.City) && provider.city}
        {has(NPF.City) && (has(NPF.State) || has(NPF.Zip)) ? ", " : null}
        {has(NPF.State) && provider.state} {has(NPF.Zip) && provider.zip}
        {has(NPF.Phone) && provider.phone ? <br /> : null}
        {has(NPF.Phone) && provider.phone ?
          <>
            <TooltipWrapper text="Phone" position="bottom">
              <i className="fa fa-phone fa-fw supress-panel-styles" />
            </TooltipWrapper>
            &nbsp; {formatPhoneNumber(provider.phone)}
          </> :
          null
        }
        {has(NPF.Fax) && provider.fax ? <br /> : null}
        {has(NPF.Fax) && provider.fax ?
          <>
            <TooltipWrapper text="Fax" position="bottom">
              <i className="fa fa-fax fa-fw supress-panel-styles" />
            </TooltipWrapper>
            &nbsp; {formatPhoneNumber(provider.fax)}
          </> :
          null
        }
      </>
    )),
    exclusionVerifiedSources: (
      <div style={{ maxWidth: "300px", whiteSpace: "normal" }}>
        {(provider as ProjectionProvider).exclusionVerifiedSources?.join("; ") ?? ""}
      </div>
    ),
    prefix: provider.prefix,
    firstName: provider.firstName,
    middleName: provider.middleName,
    lastName: provider.lastName,
    suffix: provider.suffix,
    credentials: provider.credentials,
    gender: provider.gender,
    servesPercent: provider.servesPercent !== undefined ? `${provider.servesPercent.toFixed(1)}%` : "",
    gainsPercent: provider.gainsPercent !== undefined ? `${provider.gainsPercent.toFixed(1)}%` : "---",
    impactsPercent: provider.impactsPercent !== undefined ? `${provider.impactsPercent.toFixed(1)}%` : "---",
    availability: restrictByFields(viewableFields, [NPF.DeactivatedNPI, NPF.MedicareOptOut, NPF.OIGExcluded, NPF.OIGWaiver, NPF.Precluded, NPF.InvalidNPI, NPF.MissingNPI], (has) => (
      <>
        {has(NPF.DeactivatedNPI) && provider.deactivatedNPI && <><span><i className={`supress-panel-styles fa fa-check-square-o`} /> Deactivated</span><br /></>}
        {has(NPF.MedicareOptOut) && provider.medicareOptOut && <><RoleRestricted allowedRoles={[Roles.MEDICARE_OPT_OUTS]} deniedRoles={[Roles.TOKEN_USER]}><span><i className={`supress-panel-styles fa fa-check-square-o`} /> Medicare Opt-Out</span></RoleRestricted><br /></>}
        {has(NPF.OIGExcluded) && provider.oigExcluded && <><RoleRestricted allowedRoles={[Roles.OIG]} deniedRoles={[Roles.TOKEN_USER]}><span><i className={`supress-panel-styles fa fa-check-square-o`} /> OIG Excluded</span></RoleRestricted><br /></>}
        {has(NPF.OIGWaiver) && provider.oigWaiver && <> <RoleRestricted allowedRoles={[Roles.OIG]} deniedRoles={[Roles.TOKEN_USER]}><span><i className={`supress-panel-styles fa fa-check-square-o`} /> OIG Waiver</span></RoleRestricted> <br /></>}
        {has(NPF.Precluded) && provider.precluded && <><RoleRestricted allowedRoles={[Roles.PRECLUDED_PROVIDER_USER]} deniedRoles={[Roles.TOKEN_USER]}><span><i className={`supress-panel-styles fa fa-check-square-o`} /> Medicare Precluded</span></RoleRestricted><br /></>}
        {has(NPF.InvalidNPI) && provider.invalidNPI && <><span><i className={`supress-panel-styles fa fa-check-square-o`} /> Invalid NPI</span><br /></>}
        {has(NPF.MissingNPI) && provider.missingNPI && <><span><i className={`supress-panel-styles fa fa-check-square-o`} /> Missing NPI</span><br /></>}
      </>
    )),
    taxonomyCode: provider.taxonomyCode,
    acceptingNewPatients: provider.acceptingNewPatients === undefined ? "---" : provider.acceptingNewPatients ? "Y" : "N",
    employmentStatus: provider.employmentStatus,
    contractType: provider.contractType,
    underContract: provider.underContract,
    medGroupAffiliation: provider.medGroupAffiliation,
    medicareCCN: provider.medicareCCN,
    medicareCertifiedBeds: provider.medicareCertifiedBeds,
    acceptsMedicareAssignment: provider.acceptsMedicareAssignment ? "Yes" : "",
    externZip: provider.externZip,
    externSpecialtyCode: provider.externSpecialtyCode,
    externRowId: provider.externRowId,
    externProviderId: provider.externProviderId,
    externLocationId: provider.externLocationId,
    daysSinceLastAttested: provider.daysSinceLastAttested && provider.daysSinceLastAttested <= runConfiguration.recentAttestationDays ?
      `${provider.daysSinceLastAttested} days` :
      "Not Attested",
    isRecentlyAttested: provider.isRecentlyAttested ? "Y" : "N",
    lastAttestedDate: provider.lastAttestedDate ? formatUnconvertedDateCustom(provider.lastAttestedDate, "YYYY-MM-DD") : "---",
    letterOfIntent: provider.letterOfIntent !== undefined ? provider.letterOfIntent ? "Y" : "N" : "---",
    nppesCertificationDate: provider.nppesCertificationDate ? formatUnconvertedDateCustom(provider.nppesCertificationDate, "YYYY-MM-DD") : "",
    nppesLastUpdatedDate: provider.nppesLastUpdatedDate ? formatUnconvertedDateCustom(provider.nppesLastUpdatedDate, "YYYY-MM-DD") : "",
    latitude: provider.latitude,
    longitude: provider.longitude,
    geoInfoDisplay: provider.geoInfoDisplay,
    countyFIPS: provider.countyFIPS ? provider.countyFIPS : "---",
    countySSA: provider.countySSA,
    countyName: provider.countyName,
    countyState: provider.countyState,
    mipsScore: provider.mipsScore ? provider.mipsScore.toString() : "---",
    effectivenessRank: provider.effectivenessRank ? provider.effectivenessRank.toString() : "---",
    efficiencyRank: provider.efficiencyRank ? provider.efficiencyRank.toString() : "---",
    commercialClaimsVolumeDisplay: provider.commercialClaimsVolumeDisplay ? provider.commercialClaimsVolumeDisplay : "---",
    commercialNewPatientClaims: provider.commercialNewPatientClaims ? provider.commercialNewPatientClaims : "---",
    medicaidClaimsVolumeDisplay: provider.medicaidClaimsVolumeDisplay ? provider.medicaidClaimsVolumeDisplay : "---",
    medicaidNewPatientClaims: provider.medicaidNewPatientClaims ? provider.medicaidNewPatientClaims : "---",
    medicareClaimsVolumeDisplay: provider.medicareClaimsVolumeDisplay ? provider.medicareClaimsVolumeDisplay : "---",
    medicareNewPatientClaims: provider.medicareNewPatientClaims ? provider.medicareNewPatientClaims : "---",
    vaClaimsVolumeDisplay: provider.vaClaimsVolumeDisplay ? provider.vaClaimsVolumeDisplay : "---",
    vaNewPatientClaims: provider.vaNewPatientClaims ? provider.vaNewPatientClaims : "---",
    tricareClaimsVolumeDisplay: provider.tricareClaimsVolumeDisplay ? provider.tricareClaimsVolumeDisplay : "---",
    tricareNewPatientClaims: provider.tricareNewPatientClaims ? provider.tricareNewPatientClaims : "---",
    totalClaimsVolumeDisplay: provider.totalClaimsVolumeDisplay ? provider.totalClaimsVolumeDisplay : "---",
    totalNewPatientClaims: provider.totalNewPatientClaims ? provider.totalNewPatientClaims : "---",
    locationConfidenceScoresDisplay: provider.locationConfidenceScoresDisplay ? provider.locationConfidenceScoresDisplay : "---",
    motiveScore: provider.motiveScore ? provider.motiveScore : "---",
    careJourneyScoresCostScore: provider.careJourneyScoresCostScore ? provider.careJourneyScoresCostScore : "---",
    careJourneyScoresQualityScore: provider.careJourneyScoresQualityScore ? provider.careJourneyScoresQualityScore : "---"
  };
  /* eslint-enable sort-keys */
}

// this should probably be made a generic with return type Partial<T>. the tricky part is indexing Partial<T> with a variable, i.e. matchingItem.name
const getCargoDataForProvider = (provider: ServicingProvider, viewableFields: ViewableField[], cargoColumns: Set<string>): Partial<unknown> => {
  const providerCargoData: { [x: string]: unknown } = {};
  if (cargoColumns && cargoColumns.size > 0) {
    viewableFields.forEach((column) => {
      const matchingItem = provider.cargoData.find((d) => d.name === column.defaultDisplayName);
      if (matchingItem) {
        if (matchingItem.type === OmniusCargoColumnType.bool) {
          providerCargoData[matchingItem.name] = !!matchingItem.value ? <i className="fa fa-check" /> : <></>;
        } else {
          providerCargoData[matchingItem.name] = matchingItem.value as string | number;
        }
      }
    });
  }
  return providerCargoData;
}

function getClaimsVolumeDefinitions(): JSX.Element  {
  return (
    <div className="text-left">
      Ghost: 0 patients<br />
      Peripheral: &lt;24th percentile in patient volume (based on population density and specialty)<br />
      Standard: 25th-74th percentile in patient volume (based on population density and specialty)<br />
      Core: 75th+ percentile in patient volume (based on population density and specialty)<br />
      Insufficient Data - New: There is no claims activity for this provider but because the NPI was recently issued they have not been labeled as a ghost<br />
      Insufficient Data - State/LOB: There is no claims activity for this provider but because of limited claims coverage in this State/LOB they have not been labeled as a ghost< br />
      Insufficient Data - Specialty: There is no claims activity for this provider but because of limited claims volume for this specialty they have not been labeled as a ghost<br />
      RX Only: Claims in which only Prescriptions are present<br />
    </div>
  );
}

function getEfficiencyAndEffectivenessDefinitions(npf: NPF): string {
  switch (npf) {
    case NPF.EfficiencyRank:
      return "Efficiency is a measure of physician cost vs. peers where 5 is the best score (lower cost) and 1 is the worst score (higher cost).  Efficiency measures are based on 1) cost per visit 2) cost per patient.  Standardized pricing methodology is used, meaning efficiency is a measure of comparative utilization intensity and not rate/price differences";
    case NPF.EffectivenessRank:
      return "Effectiveness is a measure of quality where 5 is the best score (higher quality) and 1 is the worst score (lower quality). Scores are relative to other providers in the same specialty and state and are derived from claims insight into 1) Mortality 2) Complications 3) Days in acute care";
    default:
      return;
  }
}

function getLocationConfidenceScoresDefinitions() {
  return (
    <div className="text-left">
      High: 85% or higher confidence practitioner IS at address <br />
      Medium: 51%-85% confidence practitioner IS at address <br />
      Low: 51%-85% confidence practitioner is NOT at address <br />
      Very Low: 85% or higher confidence practitioner is NOT at address
    </div>
  );
}

function getMotiveScoreDefinitions() {
  return (
    <div className="text-left">
      Motive Practice Wisely appropriateness tier for domain. 5 is the highest tier and is assigned to physicians 
      whose practice patterns adhere the most to clinical guidelines and evidence based medicine standards relative to their peers.
    </div>
  );
}

function getNewPatientClaimsDefinitions() {
  return (
    <div className="text-left">
      A New Patient Claims is defined as a new patient office visit which is represented by codes 99202 - 99205 on the previous 15 months of Patient Claims
    </div>
  );
}

function getCareJourneyDefinitions() {
  return (
    <div className="text-left">
      1: Performing Greatly Below Benchmark <br />
      2: Performing Below Benchmark <br />
      3: Performing at Benchmark <br />
      4: Performing Above Benchmark <br />
      5: Performing Greatly Above Benchmark
    </div>
  );
}

export {
  buildAdequacyProvidersColumnConfig,
  buildCommonImpactProviderColumns,
  getCargoDataForProvider,
  getClaimsVolumeDefinitions,
  getEfficiencyAndEffectivenessDefinitions,
  getEntityIconClasses,
  getEntityTypeDescription,
  translateNcpFilter,
  translatePercentGains,
};
